import compact from "lodash/compact";
import type { FunctionComponent } from "react";
import { useEffect, useTransition } from "react";
import { graphql, usePaginationFragment } from "react-relay";

import { topic } from "shared/data";

import type { allVideosContents } from "scmp-app/queries/__generated__/allVideosContents.graphql";
import type { videoListGridQuery$key } from "scmp-app/queries/__generated__/videoListGridQuery.graphql";

import { CardsContainer, Container, LoadMoreButton } from "./styles";
import { VideoListGridCard } from "./video-list-grid-card";

const { entityUuid: justSayingTopicUuid } = topic.justSaying as { entityUuid: string };
const topicUuids = new Set<string>([justSayingTopicUuid]);

type Props = {
  className?: string;
  filterEntityUuid?: string;
  reference: videoListGridQuery$key;
};

export const VideoListGrid: FunctionComponent<Props> = ({
  className,
  filterEntityUuid,
  reference,
}) => {
  const [isPending, startTransition] = useTransition();

  const {
    data: { contents },
    hasNext,
    loadNext,
    refetch,
  } = usePaginationFragment<allVideosContents, videoListGridQuery$key>(
    graphql`
      fragment videoListGridQuery on Query
      @argumentDefinitions(
        after: { type: "String" }
        count: { type: "Int" }
        sectionIds: { type: "[String]" }
        topicIds: { type: "[String]" }
      )
      @refetchable(queryName: "allVideosContents") {
        contents(
          after: $after
          first: $count
          filter: { sectionIds: $sectionIds, topicIds: $topicIds }
          contentTypes: [VIDEO]
          orderBy: { field: PUBLISHED_DATE, direction: DESC }
        ) @connection(key: "allVideos_contents") {
          edges {
            node {
              ... on Video {
                entityId
                ...videoListGridCardContent
              }
            }
          }
        }
      }
    `,
    reference,
  );

  useEffect(() => {
    if (filterEntityUuid === undefined) return;
    const isTopic = topicUuids.has(filterEntityUuid);
    startTransition(() => {
      refetch({
        count: 24,
        sectionIds: isTopic ? [] : compact([filterEntityUuid]),
        topicIds: isTopic ? compact([filterEntityUuid]) : [],
      });
    });
  }, [filterEntityUuid, refetch, startTransition]);

  return (
    <Container $isPending={isPending} className={className}>
      <CardsContainer>
        {contents.edges.map(({ node }) => (
          <VideoListGridCard key={node.entityId} reference={node} />
        ))}
      </CardsContainer>

      {hasNext && <LoadMoreButton onClick={() => loadNext(12)}>Watch More</LoadMoreButton>}
    </Container>
  );
};
